import { memo } from "react";

/**
 * A reusable component for displaying Statistic cards.
 *
 * @param {{ children: React.ReactNode, title: string, number: number|string, prefix?: string, description?: string, isLoading?: boolean }} props
 * @returns {JSX.Element}
 */
const GradientCard = ({
  children,
  title,
  number,
  prefix,
  description,
  isLoading = false,
}) => {
  const numberElement = number ? (
    <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-300 sm:text-2xl">
      {number}
      {prefix && (
        <span className="mx-1 text-sm font-medium text-gray-500 dark:text-gray-400">
          {prefix}
        </span>
      )}
      {description && (
        <p className="mt-0.5 text-sm font-normal text-gray-500 dark:text-gray-400 sm:text-base">
          {description}
        </p>
      )}
    </span>
  ) : null;

  return (
    <div className="card group relative mb-0 flex items-center gap-3 overflow-hidden p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-purple-900/5 md:gap-5 md:p-4">
      {/* Decorative orb */}
      <span
        aria-hidden
        className="pointer-events-none absolute -end-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br from-purple-200/40 to-transparent blur-2xl transition-opacity duration-300 group-hover:opacity-100 dark:from-purple-700/25"
      />

      {/* Icon chip */}
      <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100/60 text-purple-900 ring-1 ring-purple-100 transition-transform duration-300 group-hover:scale-105 dark:from-purple-950/60 dark:to-purple-900/40 dark:text-purple-300 dark:ring-purple-800/40 md:h-16 md:w-16 print:hidden">
        {/* Inner highlight */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-white/60 to-transparent dark:from-white/10"
        />
        <span className="relative">{children}</span>
      </div>

      <div className="relative flex flex-grow flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-gray-500 dark:text-gray-500 md:text-[13px]">
          {title}
        </span>
        {isLoading ? (
          <div className="h-7 w-28 animate-pulse rounded-md bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800" />
        ) : (
          numberElement
        )}
      </div>
    </div>
  );
};

export default memo(GradientCard);
